import React, { useState, useEffect, useRef, useCallback, useImperativeHandle } from 'react';
import type {
  PlayerNavProps,
  PlayerNavAPI,
  NavFileInfo,
  NavFileResponse,
} from './types';

const TITLE_MAX = 40;
const AUTO_ADVANCE_KEY = 'player.autoAdvance';

function truncateTitle(title: string): string {
  if (title.length <= TITLE_MAX) return title;
  return title.slice(0, TITLE_MAX - 1) + '\u2026';
}

function isNonEmpty(file: NavFileResponse['file']): file is NavFileInfo {
  return !!file && typeof (file as NavFileInfo).id === 'number' && (file as NavFileInfo).id > 0;
}

function readAutoAdvance(): 'back' | 'forward' | null {
  try {
    const v = localStorage.getItem(AUTO_ADVANCE_KEY);
    if (v === 'back' || v === 'forward') return v;
  } catch { /* localStorage unavailable */ }
  return null;
}

function writeAutoAdvance(v: 'back' | 'forward' | null): void {
  try {
    if (v === null) localStorage.removeItem(AUTO_ADVANCE_KEY);
    else localStorage.setItem(AUTO_ADVANCE_KEY, v);
  } catch { /* localStorage unavailable */ }
}

const PlayerNav = React.forwardRef<PlayerNavAPI, PlayerNavProps>(
  function PlayerNav(props, ref) {
    const { fileId, linkId, initialTitle, initialThumbnailUrl } = props;

    const [currentFileId, setCurrentFileId] = useState(fileId);
    const [currentTitle, setCurrentTitle] = useState(initialTitle);
    const [currentThumb, setCurrentThumb] = useState(initialThumbnailUrl);

    const [prevFile, setPrevFile] = useState<NavFileInfo | null>(null);
    const [prevLoaded, setPrevLoaded] = useState(false);
    const [nextFile, setNextFile] = useState<NavFileInfo | null>(null);
    const [nextLoaded, setNextLoaded] = useState(false);

    const [hoveredSide, setHoveredSide] = useState<'prev' | 'next' | null>(null);
    const [autoAdvance, setAutoAdvance] = useState<'back' | 'forward' | null>(() => readAutoAdvance());

    const containerRef = useRef<HTMLDivElement>(null);
    const prevButtonRef = useRef<HTMLButtonElement>(null);
    const nextButtonRef = useRef<HTMLButtonElement>(null);
    const prevPopoverRef = useRef<HTMLDivElement>(null);
    const nextPopoverRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => ({
      getContainerElement: () => containerRef.current,
      setCurrentFile: (newFileId, newTitle, newThumbnailUrl) => {
        if (newFileId === currentFileId) return; // Click handler already updated us — no-op
        setCurrentTitle(newTitle);
        setCurrentThumb(newThumbnailUrl);
        setPrevFile(null);
        setPrevLoaded(false);
        setNextFile(null);
        setNextLoaded(false);
        setCurrentFileId(newFileId);
        setHoveredSide(null);
      },
    }), [currentFileId]);

    // Fetch a single direction. Returns nothing — sets state directly.
    const fetchDirection = useCallback(async (direction: 'prev' | 'next', forFileId: number) => {
      const params = new URLSearchParams(window.location.search);
      const searchVal = params.get('search') ?? '';
      const searchParamsVal = params.get('searchparams') ?? '';

      let url = `ajax/files.php?${direction}=${forFileId}`;
      if (linkId) url += `&link=${encodeURIComponent(linkId)}`;
      if (searchVal) url += `&search=${encodeURIComponent(searchVal)}`;
      if (searchParamsVal) url += `&searchparams=${encodeURIComponent(searchParamsVal)}`;

      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`${response.status} ${response.statusText}`);
        }
        const data: NavFileResponse = await response.json();

        // Bail if a newer fetch superseded this one
        if (forFileId !== currentFileId) return;

        if (data.error) {
          console.warn(`PlayerNav: ${direction} fetch error:`, data.error);
          if (direction === 'prev') {
            setPrevFile(null);
            setPrevLoaded(true);
          } else {
            setNextFile(null);
            setNextLoaded(true);
          }
          return;
        }

        if (isNonEmpty(data.file)) {
          if (direction === 'prev') {
            setPrevFile(data.file);
            setPrevLoaded(true);
          } else {
            setNextFile(data.file);
            setNextLoaded(true);
          }
        } else {
          // Empty file object — no neighbor in this direction
          if (direction === 'prev') {
            setPrevFile(null);
            setPrevLoaded(true);
          } else {
            setNextFile(null);
            setNextLoaded(true);
          }
        }
      } catch (err) {
        console.warn(`PlayerNav: ${direction} fetch failed:`, err);
        if (forFileId !== currentFileId) return;
        if (direction === 'prev') {
          setPrevFile(null);
          setPrevLoaded(true);
        } else {
          setNextFile(null);
          setNextLoaded(true);
        }
      }
    }, [currentFileId, linkId]);

    // Fetch directions whenever currentFileId changes — but only directions
    // that haven't been pre-populated by a click handler.
    // prevLoaded / nextLoaded are intentionally excluded from the dep array:
    // including them caused cascading re-runs (one direction completing would
    // re-trigger the effect and duplicate the other direction's fetch).
    useEffect(() => {
      if (!prevLoaded) fetchDirection('prev', currentFileId);
      if (!nextLoaded) fetchDirection('next', currentFileId);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentFileId]);

    // ── Click handlers ──

    const handleNextClick = useCallback(() => {
      if (!nextFile) return;
      const newId = nextFile.id;
      // Snapshot current → becomes new prev (no fetch needed)
      const newPrev: NavFileInfo = {
        id: currentFileId,
        title: currentTitle,
        thumbnail_url: currentThumb,
      };
      setPrevFile(newPrev);
      setPrevLoaded(true);
      // Promote next → current
      setCurrentTitle(nextFile.title);
      setCurrentThumb(nextFile.thumbnail_url);
      // Clear next so the useEffect refetches it for the new current
      setNextFile(null);
      setNextLoaded(false);
      setCurrentFileId(newId);
      setHoveredSide(null);
      // Drive the rest of the page
      const w = window as any;
      if (typeof w.loadFile === 'function') w.loadFile(newId);
    }, [nextFile, currentFileId, currentTitle, currentThumb]);

    const handlePrevClick = useCallback(() => {
      if (!prevFile) return;
      const newId = prevFile.id;
      const newNext: NavFileInfo = {
        id: currentFileId,
        title: currentTitle,
        thumbnail_url: currentThumb,
      };
      setNextFile(newNext);
      setNextLoaded(true);
      setCurrentTitle(prevFile.title);
      setCurrentThumb(prevFile.thumbnail_url);
      setPrevFile(null);
      setPrevLoaded(false);
      setCurrentFileId(newId);
      setHoveredSide(null);
      const w = window as any;
      if (typeof w.loadFile === 'function') w.loadFile(newId);
    }, [prevFile, currentFileId, currentTitle, currentThumb]);

    // ── Auto-advance toggle ──

    const handleAutoAdvanceClick = useCallback((direction: 'back' | 'forward') => {
      const newValue = autoAdvance === direction ? null : direction;
      setAutoAdvance(newValue);
      writeAutoAdvance(newValue);

      // For non-video/audio files, kick off auto-advance immediately
      if (newValue !== null) {
        const w = window as any;
        const fileType = w.jsconnect?.file?.type;
        if (fileType !== 1 && fileType !== 2 && typeof w.startAutoAdvance === 'function') {
          window.setTimeout(() => w.startAutoAdvance(), 2000);
        }
      }
    }, [autoAdvance]);

    // ── Hover popover positioning ──

    useEffect(() => {
      if (hoveredSide === 'prev') {
        const button = prevButtonRef.current;
        const popover = prevPopoverRef.current;
        if (!button || !popover) return;
        const rect = button.getBoundingClientRect();
        const popHeight = popover.offsetHeight;
        const popTop = rect.top + rect.height / 2 - popHeight / 2;
        popover.style.top = `${popTop}px`;
        popover.style.left = `${rect.right + 10}px`;
        popover.style.right = '';
      } else if (hoveredSide === 'next') {
        const button = nextButtonRef.current;
        const popover = nextPopoverRef.current;
        if (!button || !popover) return;
        const rect = button.getBoundingClientRect();
        const popHeight = popover.offsetHeight;
        const popTop = rect.top + rect.height / 2 - popHeight / 2;
        popover.style.top = `${popTop}px`;
        popover.style.right = `${window.innerWidth - rect.left + 10}px`;
        popover.style.left = '';
      }
    }, [hoveredSide]);

    // ── Render ──

    return (
      <div ref={containerRef}>
        {prevFile && (
          <>
            <div
              className="player-nav-popover left"
              ref={prevPopoverRef}
              style={{ display: hoveredSide === 'prev' ? 'block' : 'none' }}
            >
              <div className="file-thumb" style={{ backgroundImage: `url('${prevFile.thumbnail_url}')` }}></div>
              <h3>{truncateTitle(prevFile.title)}</h3>
            </div>
            <div
              className="player-nav nomobile"
              id="prev-file"
              style={{ marginLeft: '-41px', marginTop: '3px' }}
            >
              <button
                className="player-nav-button"
                ref={prevButtonRef}
                onClick={handlePrevClick}
                onMouseEnter={() => setHoveredSide('prev')}
                onMouseLeave={() => setHoveredSide(null)}
                type="button"
              >
                &lt;
              </button>
              <button
                id="auto-advance-back"
                className={`auto-advance tooltip horizontal${autoAdvance === 'back' ? ' on' : ''}`}
                title="Auto-advance back"
                onClick={() => handleAutoAdvanceClick('back')}
                type="button"
              >
                &#10226;
              </button>
            </div>
          </>
        )}
        {nextFile && (
          <>
            <div
              className="player-nav-popover right"
              ref={nextPopoverRef}
              style={{ display: hoveredSide === 'next' ? 'block' : 'none' }}
            >
              <div className="file-thumb" style={{ backgroundImage: `url('${nextFile.thumbnail_url}')` }}></div>
              <h3>{truncateTitle(nextFile.title)}</h3>
            </div>
            <div
              className="player-nav nomobile"
              id="next-file"
              style={{ right: '8px' }}
            >
              <button
                className="player-nav-button"
                ref={nextButtonRef}
                onClick={handleNextClick}
                onMouseEnter={() => setHoveredSide('next')}
                onMouseLeave={() => setHoveredSide(null)}
                type="button"
              >
                &gt;
              </button>
              <button
                id="auto-advance-forward"
                className={`auto-advance tooltip horizontal${autoAdvance === 'forward' ? ' on' : ''}`}
                title="Auto-advance forward"
                onClick={() => handleAutoAdvanceClick('forward')}
                type="button"
              >
                &#10227;
              </button>
            </div>
          </>
        )}
      </div>
    );
  }
);

export default PlayerNav;
